import { forwardRef, useState, useEffect } from 'react'
import { PHONE_VALIDATION } from '@/utils/validation'
import SaudiArabiaFlag from '@/assets/Saudi_Arabia.png'
import { ChevronDown } from 'lucide-react'
import { useLocalization } from '@/contexts/LocalizationContext'

interface Country {
  code: string
  dialCode: string
  name: string
  flag: string | null
  emoji: string
  placeholder: string
}

const COUNTRIES: Country[] = [
  {
    code: 'SA',
    dialCode: '966',
    name: 'Saudi Arabia',
    flag: SaudiArabiaFlag,
    emoji: '🇸🇦',
    placeholder: '5XXXXXXXX',
  },
  {
    code: 'EG',
    dialCode: '20',
    name: 'Egypt',
    flag: null,
    emoji: '🇪🇬',
    placeholder: '1XXXXXXXXX',
  },
]

interface PhoneInputProps {
  value: string
  onChange: (value: string) => void
  error?: string | null
  disabled?: boolean
  hideLabel?: boolean
  'aria-label'?: string
  'aria-describedby'?: string
}

/**
 * Reusable phone input component with country code selector
 */
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ value, onChange, error, disabled, hideLabel, 'aria-label': ariaLabel, 'aria-describedby': ariaDescribedBy }, ref) => {
    const { t } = useLocalization()
    const [selectedCountry, setSelectedCountry] = useState<Country>(COUNTRIES[0])
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const [localNumber, setLocalNumber] = useState('')

    // Extract local number from full value when component mounts or value changes externally
    useEffect(() => {
      if (value) {
        const stripped = value.startsWith('+') ? value.slice(1) : value
        for (const country of COUNTRIES) {
          if (stripped.startsWith(country.dialCode)) {
            setSelectedCountry(country)
            setLocalNumber(stripped.slice(country.dialCode.length))
            return
          }
        }
        setLocalNumber(stripped)
      } else {
        setLocalNumber('')
      }
    }, [])

    // Update parent with full E.164 number (+dialCode + localNumber)
    const handleLocalNumberChange = (newLocalNumber: string) => {
      const sanitized = newLocalNumber.replace(/\D/g, '')
      setLocalNumber(sanitized)
      onChange(`+${selectedCountry.dialCode}${sanitized}`)
    }

    // Handle country change
    const handleCountryChange = (country: Country) => {
      setSelectedCountry(country)
      setIsDropdownOpen(false)
      onChange(`+${country.dialCode}${localNumber}`)
    }

    return (
      <div className="space-y-2 sm:space-y-3">
        {!hideLabel && (
          <label
            htmlFor="phone-input"
            className="text-sm font-semibold text-gray-800 block"
          >
            {t('auth.phoneNumber')}
          </label>
        )}
        <div className="flex gap-2 sm:gap-3">
          {/* Country Code Selector */}
          <div className="relative">
            <button
              type="button"
              onClick={() => !disabled && setIsDropdownOpen(!isDropdownOpen)}
              disabled={disabled}
              className="flex items-center gap-1.5 sm:gap-2 border-2 border-gray-200 rounded-xl px-2 sm:px-3 py-3 bg-gradient-to-br from-white to-gray-50/50 min-w-[100px] sm:min-w-[120px] transition-all duration-200 hover:border-primary/40 hover:shadow-md focus:border-primary focus:ring-2 focus:ring-primary/20 focus:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={t('auth.selectCountry')}
              aria-expanded={isDropdownOpen}
              aria-haspopup="listbox"
            >
              {selectedCountry.flag ? (
                <img
                  src={selectedCountry.flag}
                  alt={`${selectedCountry.name} Flag`}
                  className="w-6 h-4 sm:w-8 sm:h-6 object-contain flex-shrink-0 rounded-sm shadow-sm"
                />
              ) : (
                <span className="text-xl sm:text-2xl leading-none">{selectedCountry.emoji}</span>
              )}
              <span className="text-xs sm:text-sm font-bold text-gray-800">
                +{selectedCountry.dialCode}
              </span>
              <ChevronDown className={`w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown */}
            {isDropdownOpen && (
              <div 
                className="absolute top-full start-0 mt-1 w-full min-w-[160px] bg-white border-2 border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden"
                role="listbox"
              >
                {COUNTRIES.map((country) => (
                  <button
                    key={country.code}
                    type="button"
                    onClick={() => handleCountryChange(country)}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50 transition-colors ${
                      selectedCountry.code === country.code ? 'bg-primary/5' : ''
                    }`}
                    role="option"
                    aria-selected={selectedCountry.code === country.code}
                  >
                    {country.flag ? (
                      <img
                        src={country.flag}
                        alt={`${country.name} Flag`}
                        className="w-7 h-5 object-contain flex-shrink-0 rounded-sm"
                      />
                    ) : (
                      <span className="text-xl leading-none">{country.emoji}</span>
                    )}
                    <span className="text-sm font-medium text-gray-700">
                      +{country.dialCode}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Phone Number Input */}
          <input
            ref={ref}
            id="phone-input"
            type="tel"
            inputMode="numeric"
            placeholder={selectedCountry.placeholder}
            value={localNumber}
            onChange={(e) => handleLocalNumberChange(e.target.value)}
            disabled={disabled}
            className={`flex-1 min-w-0 border-2 rounded-xl px-3 sm:px-4 py-3 text-sm sm:text-base font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:bg-gray-50 disabled:cursor-not-allowed placeholder:text-gray-400 ${
              error
                ? 'border-red-300 focus:border-red-400 bg-red-50/50'
                : 'border-gray-200 focus:border-primary bg-white hover:border-gray-300 hover:shadow-sm focus:shadow-lg'
            }`}
            maxLength={PHONE_VALIDATION.MAX_LENGTH}
            required
            aria-label={ariaLabel || t('auth.phoneNumberInput')}
            aria-describedby={ariaDescribedBy}
            aria-invalid={!!error}
            aria-errormessage={error ? 'phone-error' : undefined}
          />
        </div>
      </div>
    )
  }
)

PhoneInput.displayName = 'PhoneInput'
